import { createContext, ReactNode, useContext, useMemo } from 'react';

import { useUserJobApplications } from '@/hooks/useUserJobApplications';

import { useFilteredJobs } from '../hooks/useFilteredJobs';

type FilteredJobsContextValue = ReturnType<typeof useFilteredJobs> & {
  hasAppliedToJob: (job: { id: string }) => boolean;
  refetchApplications: () => void;
};

const FilteredJobsContext = createContext<FilteredJobsContextValue | null>(null);

interface FilteredJobsProviderProps {
  children: ReactNode;
  cvId: string | null;
}

export function FilteredJobsProvider({ children, cvId }: FilteredJobsProviderProps) {
  const filteredJobs = useFilteredJobs({ cvId });
  const { hasAppliedToJob, refetch } = useUserJobApplications();

  const contextValue = useMemo(
    () => ({
      ...filteredJobs,
      hasAppliedToJob,
      refetchApplications: refetch,
    }),
    [filteredJobs, hasAppliedToJob, refetch],
  );

  return (
    <FilteredJobsContext.Provider value={contextValue}>{children}</FilteredJobsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useFilteredJobsContext() {
  const context = useContext(FilteredJobsContext);

  if (!context) {
    throw new Error('useFilteredJobsContext must be used within FilteredJobsProvider');
  }

  return context;
}
